<template>
    <div class="px-3 mt-4">
        <div class="mt-3 mb-8 space-y-2 max-w-7xl mx-auto">
            <div
                class="md:flex space-y-3 md:space-y-0 items-center justify-between bg-gray-50 py-2 px-3"
            >
                <h4 class="text-lg text-center font-medium">Area #{{ id }}</h4>
                <div class="md:flex space-y-2 md:space-y-0 md:space-x-4 justify-end">
                    <button
                        class="w-full flex items-center justify-center border border-gray-400 hover:border-gray-900 text-sm rounded px-6 py-2 focus:outline-none focus:shadow-outline"
                        @click="editModalOpen = true"
                    >
                        <svg
                            class="w-5 h-5 text-gray-400 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            ></path>
                        </svg>
                        Edit
                    </button>
                    <button
                        class="group w-full flex items-center justify-center border text-red-800 border-red-500 hover:text-white hover:bg-red-500 text-sm rounded px-6 py-2 focus:outline-none focus:shadow-outline"
                        @click="deleteModalOpen = true"
                    >
                        <svg
                            class="w-5 h-5 group-hover:text-red-100 text-red-300 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            ></path>
                        </svg>
                        Delete
                    </button>
                </div>
            </div>
            <div v-if="area">
                <modal
                    closable="true"
                    @close="closeEditModal"
                    :title="`Update Area #${area.id}`"
                    :open="editModalOpen"
                >
                    <div slot="content">
                        <form class="space-y-4 mt-4" @submit.prevent="updateArea">
                            <div class="md:flex items-center space-y-2 md:space-y-0 md:space-x-3">
                                <section class="flex-1 space-y-1 flex flex-col">
                                    <label for="givKind" class="text-sm">Area Name (JP)</label>
                                    <input
                                        placeholder="東京"
                                        :disabled="submitting || submitted"
                                        v-model="editFormTag"
                                        name="tag"
                                        class="form-input"
                                    />
                                </section>
                                <section class="flex-1 space-y-1 flex flex-col">
                                    <label for="givKind" class="text-sm">Area Name (English)</label>
                                    <input
                                        placeholder="Tokyo"
                                        :disabled="submitting || submitted"
                                        v-model="editFormTagEn"
                                        name="tagEn"
                                        class="form-input"
                                    />
                                </section>
                            </div>
                            <p v-if="!!editError" class="my-2 error-msg">{{ editError }}</p>
                            <div class="mt-6 mb-2 flex justify-between">
                                <Button
                                    :disabled="submitting"
                                    @click="closeEditModal"
                                    class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                    type="button"
                                    label="Cancel"
                                />
                                <Button
                                    :disabled="!formValid"
                                    :loading="updating"
                                    :class="[formValid ? '' : 'pointer-events-none opacity-75']"
                                    class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded"
                                    label="Update"
                                />
                            </div>
                        </form>
                    </div>
                </modal>
                <modal @close="closeDeleteModal" :closable="!deleting" :open="deleteModalOpen">
                    <div slot="header">
                        <h2 class="pt-2 text-xl font-semibold text-red-600">
                            Delete Area #{{ area.id }}
                        </h2>
                    </div>
                    <div slot="content">
                        <form class="mt-4" @submit.prevent="deleteArea">
                            <p class="bg-red-100 rounded-sm px-3 py-4">
                                Are you sure you want to delete area id #{{ area.id }}.
                                <br />
                                <span class="uppercase tracking-wide text-sm font-medium"
                                    >This action cannot be reversed!!</span
                                >
                            </p>
                            <p v-if="!!deleteError" class="my-2 error-msg">{{ deleteError }}</p>
                            <div class="px-2 mt-6 mb-2 flex justify-between">
                                <Button
                                    :disabled="deleting"
                                    @click="closeDeleteModal"
                                    class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                    type="button"
                                    label="Cancel"
                                />
                                <Button
                                    :disabled="deleting"
                                    :loading="deleting"
                                    class="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded"
                                    label="Yes Delete"
                                />
                            </div>
                        </form>
                    </div>
                </modal>
            </div>
            <div>
                <pre><code>{{ JSON.stringify(area, null, 1) }}</code></pre>
            </div>
        </div>
    </div>
</template>
<script>
import api from '../lib/api'
export default {
    methods: {
        closeEditModal() {
            this.editModalOpen = false
            this.editError = null
            this.resetData()
        },
        closeDeleteModal() {
            this.deleteModalOpen = false
            this.deleteError = null
        },
        async updateArea() {
            this.updating = true
            const payload = {
                tag: this.formTag,
                tagEn: this.formTagEn,
            }
            try {
                await api.updateArea(this.id, payload)
                this.updating = false
                this.editModalOpen = false
            } catch (err) {
                this.editError = err.message
                this.updating = false
            }
        },
        async deleteArea() {
            this.deleting = true
            try {
                await api.deleteArea(this.id)
                this.deleting = false
                this.deleteModalOpen = false
                this.$router.replace({ path: '/areas', query: { reload: true } })
            } catch (err) {
                this.deleteError = err.message
                this.deleting = false
            }
        },
        resetData() {
            this.editFormTag = this.area.tag
            this.editFormTagEn = this.area.tagEn
        },
    },
    async mounted() {
        this.area = await api.getArea(this.id)
        console.log(this.area)
        this.formTag = this.area.tag
        this.formTagEn = this.area.tagEn
        this.loading = false
    },
    computed: {
        formValid() {
            return this.area.tag !== this.editFormTag || this.area.tagEn !== this.editFormTagEn
        },
        editFormTag: {
            get() {
                const result = this.formTag || this.area.tag
                return result
            },
            set(v) {
                this.formTag = v
            },
        },
        editFormTagEn: {
            get() {
                const result = this.formTagEn || this.area.tagEn
                return result
            },
            set(v) {
                this.formTagEn = v
            },
        },
    },
    data() {
        return {
            area: null,
            loading: true,
            formTag: '',
            formTagEn: '',
            deleting: false,
            updating: false,
            editModalOpen: false,
            deleteModalOpen: false,
            submitting: false,
            submitted: false,
            deleteError: null,
            editError: null,
            id: this.$route.params.id,
        }
    },
}
</script>
